import { supabase } from '@/lib/supabase';

// --- Types ---

export interface ActivityParams {
  // Common
  actor_name?: string;
  actor_id?: string;
  group_name?: string;
  group_id?: string;

  // Expense (stored in minor units by DB triggers — e.g. 10050 = €100.50)
  amount?: number;
  currency?: string;
  description?: string;
  expense_date?: string;
  category_id?: string;

  // Comment
  expense_description?: string;
  expense_id?: string;
  comment_text?: string;
  comment_preview?: string;
  old_text?: string;

  // Settlement
  from_user_name?: string;
  from_user_id?: string;
  to_user_name?: string;
  to_user_id?: string;
  settlement_date?: string;

  // Member
  member_name?: string;
  member_id?: string;
  role?: string;
  old_role?: string;
  new_role?: string;

  // Dispute
  dispute_id?: string;
  reason_type?: string;
  reason_text?: string;
  resolution_type?: string;
  resolution_note?: string;
  resolution_reason_type?: string;
  raised_by_name?: string;
  raised_by_id?: string;
  resolved_by_name?: string;
  resolved_by_id?: string;

  // Import (import.completed)
  count?: number;

  // Group change tracking (group.updated)
  group_changes?: Array<{
    field: string;
    old_value?: string | number | boolean | null;
    new_value?: string | number | boolean | null;
  }>;

  // Change tracking (expense.updated)
  changes?: {
    amount?: { old: number; new: number; old_currency?: string; new_currency?: string };
    description?: { old: string; new: string };
    expense_date?: { old: string; new: string };
    category?: { old_id: string | null; new_id: string | null };
    receipt?: { added?: boolean; removed?: boolean; updated?: boolean };
    notes?: { old: string | null; new: string | null };
  };
}

export interface ActivityRow {
  id: string;
  activity_type: string;
  actor_id: string;
  group_id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  params: ActivityParams;
  created_at: string;
  // Joined from groups table
  group_color?: string;
}

export type ActivityFilterCategory = 'all' | 'expenses' | 'settlements' | 'comments' | 'members';

// --- Filter mapping ---

const FILTER_TYPE_MAP: Record<Exclude<ActivityFilterCategory, 'all'>, string[]> = {
  expenses: [
    'expense', 'expense.created', 'expense.updated', 'expense.deleted',
    'dispute.raised', 'dispute.resolved', 'dispute.rejected', 'dispute.withdrawn', 'dispute.reopened',
    'import.completed',
  ],
  settlements: [
    'settlement', 'settlement.created', 'settlement.updated', 'settlement.deleted',
  ],
  comments: [
    'comment', 'comment.added', 'comment.edited', 'comment.deleted',
  ],
  members: [
    'member', 'group.member.added', 'group.member.removed', 'group.member.role.changed',
    'group.created', 'group.updated', 'group.deleted',
  ],
};

// --- Query ---

export const PAGE_SIZE = 20;

interface FetchActivitiesArgs {
  filterCategory?: ActivityFilterCategory;
  groupId?: string;
  limit?: number;
  offset?: number;
  // Non-archived group IDs for the cross-group feed. Without it the query relies
  // on RLS and leaks activities from groups the user archived.
  scopeGroupIds?: string[];
}

export async function fetchActivities({
  filterCategory = 'all',
  groupId,
  limit = PAGE_SIZE,
  offset = 0,
  scopeGroupIds,
}: FetchActivitiesArgs): Promise<ActivityRow[]> {
  let query = supabase
    .from('activities')
    .select('*, groups(color)')
    .eq('_deleted', false)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (filterCategory !== 'all') {
    const types = FILTER_TYPE_MAP[filterCategory];
    if (types) {
      query = query.in('activity_type', types);
    }
  }

  // Single-group view wins; otherwise scope the cross-group feed to the caller's
  // non-archived groups so archived-group activities don't leak.
  if (groupId) {
    query = query.eq('group_id', groupId);
  } else if (scopeGroupIds) {
    query = query.in('group_id', scopeGroupIds);
  }

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((row: any) => {
    const { groups: joinedGroups, ...rest } = row;
    return {
      ...rest,
      params: row.params ?? {},
      group_color: joinedGroups?.color ?? undefined,
    };
  });
}

export async function fetchRecentActivities(
  limit = 10,
  scopeGroupIds?: string[],
): Promise<ActivityRow[]> {
  return fetchActivities({ limit, offset: 0, scopeGroupIds });
}
